import { z } from 'zod';
/**
 * Writing a promotion (DECISIONS D70).
 *
 * Shared by both consoles. What differs between them is not the shape but the AUTHORITY:
 * a tenant may only write promotions funded by, and scoped to, themselves; the platform may
 * write one for any tenant or for all of them. Neither is expressed here — a schema that
 * decided who pays would be a permission check in the wrong layer.
 */
export const PROMOTION_KINDS = ['PERCENTAGE', 'FIXED_AMOUNT'];
export const PROMOTION_STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'ENDED'];
export const REFERRAL_ROLES = ['REFERRER', 'REFEREE'];
/** Typed by passengers, so one spelling: trimmed, upper-cased, no punctuation to mistype. */
export const promotionCodeSchema = z
    .string()
    .trim()
    .min(3)
    .max(24)
    .regex(/^[A-Za-z0-9-]+$/, 'Use letters, numbers and hyphens only')
    .transform((value) => value.toUpperCase());
const isoDate = z.string().datetime().optional().or(z.literal(''));
export const promotionWriteSchema = z
    .object({
    code: promotionCodeSchema,
    name: z.string().trim().min(2).max(60),
    description: z.string().trim().max(300).optional().or(z.literal('')),
    kind: z.enum(PROMOTION_KINDS),
    /** 500 = 5%. 10000 is a free ride, which is the ceiling. */
    percentBps: z.number().int().min(1).max(10_000).optional(),
    amountCents: z.number().int().min(1).optional(),
    currency: z.string().trim().length(3).toUpperCase().optional().or(z.literal('')),
    /** How many bookings one passenger may use it on. This is what "next 5 rides" means. */
    usesPerCustomer: z.number().int().min(1).max(100).default(1),
    /** How many passengers may claim it at all. Absent is no ceiling. */
    maxRedemptions: z.number().int().min(1).optional(),
    /**
     * How many days a granted reward stays usable, counted from when it is given. Absent
     * means it lives as long as the campaign does.
     *
     * Capped at two years: a validity nobody will outlive is the same as no validity, and
     * an unbounded integer here is a date arithmetic overflow waiting to happen.
     */
    grantValidityDays: z.number().int().min(1).max(730).optional(),
    status: z.enum(PROMOTION_STATUSES).default('DRAFT'),
    startsAt: isoDate,
    endsAt: isoDate,
    /** Marks this as an arm of the referral programme rather than a code anyone can type. */
    referralRole: z.enum(REFERRAL_ROLES).optional(),
})
    .superRefine((value, ctx) => {
    // Each kind carries its own amount, and a row missing it would silently discount nothing
    // — a promotion that looks live in the console and does nothing in the app.
    if (value.kind === 'PERCENTAGE' && value.percentBps === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['percentBps'],
            message: 'A percentage promotion needs a percentage',
        });
    }
    if (value.kind === 'FIXED_AMOUNT') {
        if (value.amountCents === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['amountCents'],
                message: 'A fixed-amount promotion needs an amount',
            });
        }
        // "2000 off" means nothing without saying off what. The booking currency is checked
        // again at redemption; this stops the row being created ambiguous in the first place.
        if (!value.currency) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['currency'],
                message: 'A fixed-amount promotion needs a currency',
            });
        }
    }
    if (value.startsAt && value.endsAt && value.endsAt <= value.startsAt) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['endsAt'],
            message: 'The end must come after the start',
        });
    }
});
/**
 * What the platform console posts. The tenant's own console uses the base schema, because it
 * has no choice of target: itself.
 *
 * `tenantId` names who the campaign is FOR. It is not, and must never become, who PAYS for
 * it -- the funder is decided by which route the request arrived on, so that an operator
 * cannot mark their own discounts platform-funded and bill somebody else for them.
 */
export const promotionPlatformWriteSchema = promotionWriteSchema.and(z.object({
    /** Absent or empty means every tenant. */
    tenantId: z.string().trim().min(1).optional().or(z.literal('')),
}));
/** A passenger typing a code, either at checkout or on the offers screen. */
export const redeemCodeSchema = z.object({ code: promotionCodeSchema });
//# sourceMappingURL=promotion.js.map